/**
 * Public password reset completion — `/reset-password/:token`.
 */
import {
	Alert,
	Box,
	Button,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useState, type FormEvent } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { validatePassword } from "../auth/passwordPolicy";
import { getApiClient } from "../services/apiClientProvider";

export function ResetPasswordPage() {
	const { token } = useParams<{ token: string }>();
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [done, setDone] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	async function onSubmit(e: FormEvent) {
		e.preventDefault();
		setError(null);
		if (!token) {
			setError("Missing reset token.");
			return;
		}
		const policy = validatePassword(password);
		if (policy) {
			setError(policy.message);
			return;
		}
		if (password !== confirm) {
			setError("Passwords do not match.");
			return;
		}
		setSubmitting(true);
		try {
			await getApiClient().resetPassword({ token, password });
			setDone(true);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Reset failed.");
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Box data-testid="reset-password-surface">
			<Stack spacing={1} sx={{ mb: 3 }}>
				<Typography
					variant="overline"
					sx={{ color: "primary.main", letterSpacing: "0.12em", fontWeight: 700 }}
				>
					Account recovery
				</Typography>
				<Typography component="h1" variant="h4">
					Choose a new password
				</Typography>
			</Stack>

			{done ? (
				<Stack spacing={2}>
					<Alert severity="success" role="status">
						Your password has been updated. Sign in with your new password.
					</Alert>
					<Button component={RouterLink} to="/login" variant="contained">
						Sign in
					</Button>
				</Stack>
			) : (
				<form noValidate onSubmit={onSubmit}>
					<Stack spacing={2.25}>
						{error && (
							<Alert severity="error" role="alert">
								{error}
							</Alert>
						)}
						<TextField
							label="New password"
							type="password"
							autoComplete="new-password"
							required
							fullWidth
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
						<TextField
							label="Confirm password"
							type="password"
							autoComplete="new-password"
							required
							fullWidth
							value={confirm}
							onChange={(e) => setConfirm(e.target.value)}
						/>
						<Button
							type="submit"
							variant="contained"
							size="large"
							fullWidth
							disabled={submitting}
						>
							{submitting ? "Updating…" : "Update password"}
						</Button>
					</Stack>
				</form>
			)}
		</Box>
	);
}
